import React, { useState, useEffect } from 'react';
import { Bell, Calendar, Heart, FileText, Syringe, Shield, Save, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationPreferences = ({ token }) => {
  const [preferences, setPreferences] = useState({
    email_appointment_7d: true,
    email_appointment_1d: true,
    email_account_updates: true,
    email_carnet_updates: true,
    email_vaccination_reminders: true,
    push_enabled: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch preferences
  const fetchPreferences = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
    setIsLoading(false);
  };

  // Save preferences
  const savePreferences = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });
      if (response.ok) {
        toast.success('Préférences enregistrées');
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setIsSaving(false);
  };

  useEffect(() => {
    fetchPreferences();
  }, [token]);

  const togglePreference = (key) => {
    setPreferences({ ...preferences, [key]: !preferences[key] });
  };

  const PreferenceItem = ({ prefKey, icon: Icon, color, title, description }) => (
    <div 
      className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-colors cursor-pointer"
      onClick={() => togglePreference(prefKey)}
    >
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-xl ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h4 className="font-medium text-gray-800">{title}</h4>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
      <div 
        className={`w-12 h-7 rounded-full p-1 transition-colors ${
          preferences[prefKey] ? 'bg-green-500' : 'bg-gray-300'
        }`}
      >
        <div 
          className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
            preferences[prefKey] ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-green-100 rounded-xl">
          <Bell className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Préférences de notifications</h2>
          <p className="text-gray-500 text-sm">Gérez vos notifications par email</p>
        </div>
      </div>

      {/* Appointment Reminders Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider px-1">
          Rappels de rendez-vous
        </h3>
        <PreferenceItem
          prefKey="email_appointment_7d"
          icon={Calendar}
          color="bg-blue-500"
          title="Rappel 7 jours avant"
          description="Recevez un email une semaine avant votre rendez-vous"
        />
        <PreferenceItem
          prefKey="email_appointment_1d"
          icon={Calendar}
          color="bg-orange-500"
          title="Rappel 24 heures avant"
          description="Recevez un email la veille de votre rendez-vous"
        />
      </div>

      {/* Health Record Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider px-1">
          Carnet de santé
        </h3>
        <PreferenceItem
          prefKey="email_carnet_updates"
          icon={Heart}
          color="bg-purple-500"
          title="Mises à jour du carnet"
          description="Documents ajoutés, partages acceptés..."
        />
        <PreferenceItem
          prefKey="email_vaccination_reminders"
          icon={Syringe}
          color="bg-pink-500"
          title="Rappels de vaccination"
          description="Soyez notifié des rappels de vaccins à venir"
        />
      </div>

      {/* Account Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider px-1">
          Compte
        </h3>
        <PreferenceItem
          prefKey="email_account_updates"
          icon={Shield}
          color="bg-green-500"
          title="Mises à jour du compte"
          description="Validation de compte, changements de sécurité..."
        />
      </div>

      {/* Save Button */}
      <div className="pt-4 border-t border-gray-100">
        <Button 
          onClick={savePreferences}
          disabled={isSaving}
          className="w-full bg-green-500 hover:bg-green-600 text-white"
          data-testid="save-notification-preferences"
        >
          {isSaving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Enregistrer les préférences
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default NotificationPreferences;
